import React, { useState, useEffect } from 'react';
import { usePwaInstallPrompt, isIOS, isPwaInstalled, detectBrowser } from '../hooks/usePwaInstallPrompt';
import { useTranslation } from '../i18n';
import { Button } from './ui/button';
import { 
  X, 
  Download, 
  Share,
  Smartphone,
  Plus
} from 'lucide-react';

/**
 * Mobile PWA Install Banner
 * Shows a prominent banner on mobile devices encouraging app installation
 */
export default function MobileInstallBanner() {
  const { canInstall, isInstalled, promptInstall } = usePwaInstallPrompt();
  const [dismissed, setDismissed] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showOperaInstructions, setShowOperaInstructions] = useState(false);
  const { t } = useTranslation();
  const browser = detectBrowser();
  
  // Check if mobile device - immediate check on every render
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Force re-render on resize
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const handleResize = () => forceUpdate(n => n + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if banner was previously dismissed (with 24h expiry)
  useEffect(() => {
    const dismissedAt = localStorage.getItem('pwa-banner-dismissed');
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const hoursSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60);
      // Show again after 24 hours
      if (hoursSinceDismissed < 24) {
        setDismissed(true);
      } else {
        localStorage.removeItem('pwa-banner-dismissed');
      }
    }
  }, []);

  // Handle dismiss
  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
    localStorage.setItem('pwa-banner-seen', 'true');
  };

  // Handle install click
  const handleInstall = async () => {
    localStorage.setItem('pwa-banner-seen', 'true');
    if (isIOS()) {
      setShowIOSInstructions(true);
    } else if (browser === 'opera' && !canInstall) {
      // Opera mobile: beforeinstallprompt may not have fired yet — show manual instructions
      setShowOperaInstructions(true);
    } else {
      const result = await promptInstall();
      if (result.outcome === 'accepted') {
        handleDismiss();
      }
    }
  };

  // Don't show if:
  // - User dismissed it
  // - Not on mobile
  if (!isMobile || dismissed) {
    return null;
  }
  
  // Don't show if already installed as PWA
  if (isInstalled || isPwaInstalled()) {
    return null;
  }

  // Show for iOS users (always - they need manual install instructions)
  // Show for Opera mobile (always - Opera hides its install in the menu)
  // Show for Android/other mobile users if browser supports PWA install
  // Also show on first visit to mobile site as an intro
  const isFirstVisit = !localStorage.getItem('pwa-banner-seen');
  const isOperaMobile = browser === 'opera';
  const shouldShow = isIOS() || isOperaMobile || canInstall || isFirstVisit;
  
  if (!shouldShow) {
    return null;
  }

  return (
    <>
      {/* Main Install Banner */}
      <div className="fixed bottom-16 left-0 right-0 z-[100] md:hidden animate-in slide-in-from-bottom duration-300">
        <div className="mx-3 bg-gradient-to-r from-primary to-primary/90 text-primary-foreground p-4 shadow-xl rounded-xl border border-primary/20">
          <div className="flex items-center gap-3">
            {/* App Icon */}
            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Smartphone className="h-6 w-6" />
            </div>
            
            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">
                {t('pwa.banner.title') || 'Get the PocketFiler App'}
              </p>
              <p className="text-xs text-primary-foreground/80 truncate">
                {t('pwa.banner.subtitle') || 'Faster access, works offline'}
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                size="sm"
                variant="secondary"
                onClick={handleInstall}
                className="gap-1.5 bg-white text-primary hover:bg-white/90 font-medium"
              >
                <Download className="h-4 w-4" />
                {t('pwa.banner.install') || 'Install'}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                onClick={handleDismiss}
                className="h-8 w-8 text-primary-foreground/70 hover:text-primary-foreground hover:bg-white/10"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Opera Mobile Install Instructions Modal
          Opera mobile supports "Add to Home Screen" via its own menu (the + icon
          or the tab list button → "Add page to" → "Home screen"). */}
      {showOperaInstructions && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center animate-in fade-in duration-200">
          <div className="bg-background w-full max-w-lg rounded-t-2xl p-6 pb-8 animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                {t('pwa.opera.mobileTitle')}
              </h3>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setShowOperaInstructions(false)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <p className="text-muted-foreground text-sm mb-6">
              {t('pwa.opera.mobileDescription')}
            </p>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                  1
                </div>
                <div className="pt-1 text-sm">
                  {t('pwa.opera.mobileStep1')}{' '}
                  <strong>⊕</strong>{' '}
                  {t('pwa.opera.mobileStep1b')}
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                  2
                </div>
                <div className="pt-1 text-sm">
                  {t('pwa.opera.mobileStep2')}{' '}
                  <strong>{t('pwa.opera.mobileAddPage')}</strong>{' '}
                  → <strong>{t('pwa.opera.mobileHomeScreen')}</strong>
                </div>
              </div>
            </div>

            <Button
              className="w-full mt-6"
              onClick={() => setShowOperaInstructions(false)}
            >
              {t('pwa.ios.gotIt')}
            </Button>
          </div>
        </div>
      )}

      {/* iOS Install Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-end justify-center animate-in fade-in duration-200">
          <div className="bg-background w-full max-w-lg rounded-t-2xl p-6 pb-8 animate-in slide-in-from-bottom duration-300">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg">
                {t('pwa.ios.title') || 'Install PocketFiler'}
              </h3>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => setShowIOSInstructions(false)}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <p className="text-muted-foreground text-sm mb-6">
              {t('pwa.ios.description') || 'Add PocketFiler to your home screen for the best experience:'}
            </p>
            
            <div className="space-y-4">
              {/* Step 1 */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                  1
                </div>
                <div className="pt-1">
                  <p className="text-sm">
                    {t('pwa.ios.step1') || 'Tap the'} <Share className="h-4 w-4 inline-block mx-1 text-primary" /> {t('pwa.ios.shareButton') || 'Share button'} {t('pwa.ios.inToolbar') || 'in your browser toolbar'}
                  </p>
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                  2
                </div>
                <div className="pt-1">
                  <p className="text-sm">
                    {t('pwa.ios.step2') || 'Scroll down and tap'} <span className="font-medium">"Add to Home Screen"</span>
                  </p>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-sm">
                  3
                </div>
                <div className="pt-1">
                  <p className="text-sm">
                    {t('pwa.ios.step3') || 'Tap'} <span className="font-medium">"Add"</span> {t('pwa.ios.inTopRight') || 'in the top right corner'}
                  </p>
                </div>
              </div>
            </div>
            
            <Button 
              className="w-full mt-6" 
              onClick={() => setShowIOSInstructions(false)}
            >
              {t('pwa.ios.gotIt') || 'Got it'}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
